/**
 * Résolution de flux YouTube EN LOCAL sur l'appareil (pont natif Android,
 * NewPipeExtractor — même principe que SmartTube/NewPipe) : CHAQUE appareil
 * extrait ses propres URLs directement depuis YouTube, avec SA PROPRE IP.
 * Aucun quota centralisé partagé (contrairement au serveur + RapidAPI, qui se
 * fait bannir pour TOUS les utilisateurs en même temps dès qu'un seul dépasse
 * le quota).
 *
 * N'existe QUE dans l'APK (window.AndroidVideoResolver injecté nativement) —
 * sur le web/desktop, resolveNative() renvoie toujours null → l'appelant
 * retombe sur l'API serveur habituelle, sans aucun changement de comportement.
 */

type NativeResolveResult = {
  ok: boolean
  audioUrl?: string | null
  videoUrl?: string | null
  title?: string
  durationSec?: number
  error?: string
}

declare global {
  interface Window {
    AndroidVideoResolver?: { resolveStream: (videoId: string, requestId: string) => void }
    __blcVideoResolved?: (requestId: string, payloadB64: string) => void
  }
}

const pending = new Map<string, (r: NativeResolveResult) => void>()

if (typeof window !== 'undefined' && !window.__blcVideoResolved) {
  window.__blcVideoResolved = (requestId: string, payloadB64: string) => {
    const cb = pending.get(requestId)
    pending.delete(requestId)
    if (!cb) return
    try {
      // atob → octets latin1 → UTF-8 (le JSON peut contenir des accents/emoji
      // dans le titre) : decodeURIComponent(escape(...)) est le repli fiable
      // en environnement WebView sans TextDecoder garanti sur toutes versions.
      const bin = atob(payloadB64)
      let json: string
      try { json = decodeURIComponent(escape(bin)) } catch { json = bin }
      cb(JSON.parse(json))
    } catch {
      cb({ ok: false, error: 'parse' })
    }
  }
}

/** true si le pont natif est dispo (on tourne dans l'APK). */
export function hasNativeResolver(): boolean {
  return typeof window !== 'undefined' && !!window.AndroidVideoResolver
}

/** Résout un videoId EN LOCAL. Renvoie null si le pont est absent, échoue, ou
 *  dépasse le délai — l'appelant doit alors retomber sur l'API serveur.
 *  `onDiag` (optionnel) reçoit le détail EXACT de l'échec (timeout vs erreur
 *  native précise vs pont absent) — sans ça impossible de distinguer un vrai
 *  timeout d'un échec instantané côté NewPipeExtractor. */
export function resolveNative(
  videoId: string,
  timeoutMs = 12000,
  onDiag?: (info: { phase: 'no-bridge' | 'timeout' | 'native-error' | 'ok'; elapsedMs: number; error?: string }) => void,
): Promise<NativeResolveResult | null> {
  return new Promise((resolve) => {
    const t0 = Date.now()
    if (typeof window === 'undefined' || !window.AndroidVideoResolver) {
      onDiag?.({ phase: 'no-bridge', elapsedMs: 0 })
      resolve(null)
      return
    }
    const requestId = Math.random().toString(36).slice(2) + Date.now().toString(36)
    const timer = setTimeout(() => {
      pending.delete(requestId)
      onDiag?.({ phase: 'timeout', elapsedMs: Date.now() - t0 })
      resolve(null)
    }, timeoutMs)
    pending.set(requestId, (r) => {
      clearTimeout(timer)
      const elapsedMs = Date.now() - t0
      if (r && r.ok) { onDiag?.({ phase: 'ok', elapsedMs }); resolve(r); return }
      onDiag?.({ phase: 'native-error', elapsedMs, error: r?.error || '?' })
      resolve(null)
    })
    try {
      window.AndroidVideoResolver.resolveStream(videoId, requestId)
    } catch (e: any) {
      clearTimeout(timer)
      pending.delete(requestId)
      onDiag?.({ phase: 'native-error', elapsedMs: Date.now() - t0, error: 'throw:' + (e?.message || '?') })
      resolve(null)
    }
  })
}
